import React, { useEffect, useState } from "react";
import { isEmpty as _isEmpty } from "lodash-es";
import { connect } from "react-redux";
import { Route, Switch } from "react-router";
import { Redirect } from "react-router-dom";
import PropTypes from "prop-types";

import { fetchUpdates } from "../actions/sync";
import { fetchUser } from "../actions/user";
import DebuggingAlert from "../components/DebuggingAlert";

import HeaderBar from "../components/HeaderBar";
import { AnonymousRoute, AuthenticatedRoute } from "../components/route";

import ChangePassword from "./ChangePassword";
import Help from "./Help";
import Home from "./Home";
import Login from "./Login";
import Logout from "./Logout";
import Humans from "./notes/Humans";
import Register from "./Register";
import ResetPassword from "./ResetPassword";
import SetPassword from "./SetPassword";
import SyncPushService from "../services/SyncPushService";
import Settings from "./Settings";
import SyncPullService from "../services/SyncPullService";
import Timeline from "./Timeline";
import Unsubscribe from "./Unsubscribe";
import ChangeEmail from "./ChangeEmail";
import Circles from "./Circles";
import LoginModalService from "../services/LoginModalService";
import ServiceWorkerUpdateService from "../services/ServiceWorkerUpdateService";
import Loading from "../components/Loading";
import MobileNavbar from "../components/MobileNavbar";
import NewHumanRedirect from "./NewHumanRedirect";
import { getPostAuthRedirectPath } from "../utils";

const mapStateToProps = (state) => ({
  r: state.router, // hack for rerendering
  user: state.user,
  hasHumans: !_isEmpty(state.humans),
});

const mapDispatchToProps = (dispatch) => ({
  fetchUser: () => dispatch(fetchUser()),
  fetchUpdates: () => dispatch(fetchUpdates()),
});

const App = (props) => {
  const { user, hasHumans, fetchUser, fetchUpdates, history } = props;

  const [loadingData, setLoadingData] = useState(true);

  const isReady = (!_isEmpty(user) && hasHumans) || !loadingData;

  const postAuthRedirectPath = getPostAuthRedirectPath();

  useEffect(() => {
    fetchUser().then((action) => {
      if (action.error) {
        setLoadingData(false);
        return;
      }
      fetchUpdates().then(
        () => {
          setLoadingData(false);
        },
        () => {
          setLoadingData(false);
        },
      );
    });
  }, [fetchUser, fetchUpdates]);

  return (
    <div className="bg-gray-100 min-h-screen">
      <ServiceWorkerUpdateService />

      {user.loadLoginModalService && <LoginModalService />}

      {isReady && user.isAuthenticated ? (
        <>
          <SyncPullService />
          <SyncPushService />
        </>
      ) : null}

      {props.debugging ? <DebuggingAlert /> : null}

      <HeaderBar history={history} user={user} />

      {!isReady && <Loading fullPage text="Humans" />}

      <div className="flex flex-1 max-w-5xl mx-auto px-2 sm:px-6 lg:px-8 bg-gray-100 lg:pt-16 pb-16 lg:pb-0">
        {isReady && (
          <Switch>
            <AnonymousRoute
              path="/"
              exact
              user={user}
              to_if_authenticated={postAuthRedirectPath}
              component={Home}
            />
            <Route path="/help/" exact component={Help} />

            <AuthenticatedRoute
              exact
              path="/h/"
              user={user}
              component={NewHumanRedirect}
            />

            <AuthenticatedRoute
              exact
              path="/h/list"
              user={user}
              component={Humans}
            />

            <AuthenticatedRoute
              path="/h/new/:humanId([0-9a-f]+)"
              user={user}
              component={Humans}
            />

            <AuthenticatedRoute
              path="/h/:humanId([0-9a-f]+)"
              user={user}
              component={Humans}
            />

            <AuthenticatedRoute
              path="/t/"
              exact
              user={user}
              component={Timeline}
            />

            <AuthenticatedRoute
              path="/c/"
              exact
              user={user}
              component={Circles}
            />

            <AuthenticatedRoute
              path="/settings"
              exact
              user={user}
              component={Settings}
            />
            <AuthenticatedRoute
              path="/change-password"
              exact
              user={user}
              component={ChangePassword}
            />

            <AuthenticatedRoute
              path="/change-email"
              exact
              user={user}
              component={ChangeEmail}
            />

            {/* Login and Register pages handle redirection themselves */}
            {/* to wait for data preloading before redirect */}
            <Route path="/sign-in" exact component={Login} />
            <Route path="/sign-up" exact component={Register} />
            <AuthenticatedRoute
              path="/logout"
              exact
              user={user}
              component={Logout}
            />
            <AnonymousRoute
              path="/reset-password"
              exact
              user={user}
              to_if_authenticated="/h/"
              component={ResetPassword}
            />
            <AnonymousRoute
              path="/reset-password/:token"
              exact
              user={user}
              to_if_authenticated="/h/"
              component={SetPassword}
            />
            <Route path="/unsubscribe/:key" exact component={Unsubscribe} />

            {/* Fix for Android Chrome "Add to Home screen" */}
            <Route path="/index.html" render={() => <Redirect to="/" />} />

            <Route render={() => <h2 className="text-center">Not found</h2>} />
          </Switch>
        )}
      </div>
      {user.isAuthenticated && <MobileNavbar user={user} />}
    </div>
  );
};

App.propTypes = {
  children: PropTypes.any,
  user: PropTypes.object.isRequired,
  fetchUser: PropTypes.func.isRequired,
  fetchUpdates: PropTypes.func.isRequired,
  debugging: PropTypes.bool.isRequired,
  history: PropTypes.any.isRequired,
  hasHumans: PropTypes.bool.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
